import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';

export default function AdminLogin() {
  const [identifier, setIdentifier] = useState(''); // email or phone
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  // Detect whether input looks like an email or phone
  const isEmail = (val) => val.includes('@');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = isEmail(identifier)
        ? { email: identifier.trim().toLowerCase(), password }
        : { phone: identifier.trim(), password };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/login`,
        payload
      );

      const { token, user } = response.data;

      if (user.role !== 'admin' && user.role !== 'super_admin') {
        setError('Access denied. Admin credentials required.');
        setLoading(false);
        return;
      }

      localStorage.setItem('adminAuthToken', token);
      localStorage.setItem('adminUser', JSON.stringify(user));
      login(user, token);
      navigate('/admin', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Login failed. Please check your credentials.'
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#294c79] via-[#1f3a5c] to-[#0f1f38] flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="/Logo.jpg"
              alt="LiteRent Logo"
              className="h-24 w-auto mx-auto mb-4 bg-white rounded-2xl p-2"
            />
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-white/70">Sign in to access the admin dashboard</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#294c79] mb-2">Welcome Back</h2>
            <p className="text-[#1a1a1a]/70">Enter your admin credentials</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email or Phone */}
            <div>
              <label className="block text-sm font-medium text-[#294c79] mb-2">
                Email or Phone Number
              </label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com or 08012345678"
                required
                className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250] transition"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#294c79] mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl focus:outline-none focus:border-[#a5c250] transition"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 rounded-xl font-semibold text-white transition ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#294c79] hover:bg-[#1f3a5c] shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In to Admin Portal'
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-[#1a1a1a]/70">
              Not an admin?{' '}
              <Link to="/login" className="text-[#a5c250] font-medium hover:underline">
                Tenant Login
              </Link>
            </p>
            <p className="text-center text-sm text-[#1a1a1a]/70 mt-2">
              <Link to="/" className="text-[#294c79] font-medium hover:underline">
                ← Back to Home
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center text-white/60 text-xs">
          <p>🔒 Secure admin portal - Unauthorized access is prohibited</p>
        </div>
      </div>
    </div>
  );
}